import {useEffect} from 'react'
import {Link as RouterLink} from 'react-router-dom'

import AppsIcon from '@mui/icons-material/Apps'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Fade from '@mui/material/Fade'
import Grid from '@mui/material/Grid'
import HubIcon from '@mui/icons-material/Hub'
import Link from '@mui/material/Link'
import MailboxIcon from '@mui/icons-material/MarkunreadMailbox'
import PeopleIcon from '@mui/icons-material/People'
import SettingsIcon from '@mui/icons-material/SettingsSuggest'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import WorkIcon from '@mui/icons-material/WorkHistory'

import Loader from '../shared/Loader'

import {connect} from '../../state/hooks/tools'
import {isEmpty} from '../../lib/tools'
import {createResource, readEndpoint} from '../../lib/asyncMethods'
import {CLIENTS, INVITES, USERS, APPLICATIONS, FAKTORY, OAUTH} from '../../state/actionTypes'

import './adminController.scss'

const QUICK_LINKS = [
  {label: 'Users', icon: PeopleIcon, path: 'users', metaKey: 'usersMeta'},
  {label: 'Clients', icon: SettingsIcon, path: 'clients', metaKey: 'clientsMeta'},
  {label: 'Invitations', icon: MailboxIcon, path: 'invites', metaKey: 'invitesMeta'},
]

const AdminController = props => {
  useEffect(() => {
    if (!props.isLoading && isEmpty(props.usersMeta) && isEmpty(props.clientsMeta) && isEmpty(props.invitesMeta)) {
      props.fetchClients('clients?page=1')
      props.fetchInvites('invites?page=1')
      props.fetchUsers('users?page=1')
    }
  }, [props.usersMeta, props.clientsMeta, props.invitesMeta, props.isLoading])

  useEffect(() => {
    if (isEmpty(props.applications))
      props.readApplications('metadata/applications')

    if (isEmpty(props.scopes))
      props.readScopes('oauth-scopes')
  }, [])

  // The dashboard is behind the auth API rather than published, so it is reached with a
  // single-use ticket that the API trades for a session of its own.
  const openBackgroundJobs = () => {
    props.createFaktorySession({endpoint: 'faktory/sessions'}).then(response => {
      if (response.success)
        window.open(`${API_HOST}/faktory/enter?ticket=${encodeURIComponent(response.data)}`, '_blank', 'noopener')
    })
  }

  return (
    <Loader loading={isEmpty(props.currentUser)}>
      <Stack component='div' className='admin-controller'>
        <Fade in timeout={1000}>
          <Typography variant='h5'>Welcome {props.currentUser?.firstName}</Typography>
        </Fade>
        <Divider />
        <Box sx={{ width: '100%' }}>
          <Grid container rowSpacing={1} columnSpacing={{xs: 1, sm: 2, md: 3}}>
            {
              QUICK_LINKS.map((data, index) => {
                const DataIcon = data.icon

                return (
                  <Grid key={index} size={6}>
                    <Link component={RouterLink} to={data.path}>
                      <Paper sx={{display: 'flex', justifyContent: 'space-between'}} className='card' elevation={0}>
                        <Stack>
                          <DataIcon color='success' fontSize='large' />
                          <Typography variant='h6'>{data.label}</Typography>
                        </Stack>
                        <Typography variant='h2'>{props[data.metaKey]?.count}</Typography>
                      </Paper>
                    </Link>
                  </Grid>
                )
              })
            }
          </Grid>
        </Box>
        <Divider />
        <Typography variant='h5' gutterBottom>Metadata</Typography>
        <Link component={RouterLink} to='applications'>
          <Paper sx={{display: 'flex', justifyContent: 'space-between'}} elevation={0}>
            <Stack>
              <AppsIcon color='success' fontSize='large' />
              <Typography variant='h6'>Applications</Typography>
            </Stack>
            <Typography variant='h2'>{props.applications?.length || 0}</Typography>
          </Paper>
        </Link>
        <Divider />
        <Typography variant='h5' gutterBottom>OAuth Scopes</Typography>
        <Link component={RouterLink} to='oauth-scopes'>
          <Paper sx={{display: 'flex', justifyContent: 'space-between'}} elevation={0}>
            <Stack>
              <HubIcon color='success' fontSize='large' />
              <Typography variant='h6'>Scopes</Typography>
            </Stack>
            <Typography variant='h2'>{props.scopes?.length || 0}</Typography>
          </Paper>
        </Link>
        <Divider />
        <Typography variant='h5' gutterBottom>Background Jobs</Typography>
        <Link component='button' onClick={openBackgroundJobs}>
          <Paper sx={{display: 'flex', justifyContent: 'space-between'}} elevation={0}>
            <Stack>
              <WorkIcon color='success' fontSize='large' />
              <Typography variant='h6'>Faktory</Typography>
            </Stack>
          </Paper>
        </Link>
      </Stack>
    </Loader>
  )
}

const mapStateToProps = ({users, invites, clients, metadata, oauth}) => ({
  ...users,
  ...invites,
  ...clients,
  ...metadata,
  ...oauth,
  isLoading: !!(users.loading || invites.loading || clients.loading)
})

const actionCreators = ({users, invites, clients, metadata, oauth, faktory}) => ({
  fetchUsers: readEndpoint(users, USERS),
  fetchInvites: readEndpoint(invites, INVITES),
  fetchClients: readEndpoint(clients, CLIENTS),
  readApplications: readEndpoint(metadata, APPLICATIONS),
  readScopes: readEndpoint(oauth, OAUTH),
  createFaktorySession: createResource(faktory, FAKTORY)
})

export default connect(mapStateToProps, actionCreators)(AdminController)
